import React from "react";
import AccordionEditable from "../../../../../components/ui/accordions/AccordionEditable";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import DocumentList from "../../../../../components/ui/forms/DocumentList";

const InsuranceSection = ({
  filing,
  documents,
  isCpa,
  handleOpenUploadModal,
  handleDocumentDeleted,
  allEfilesComplete,
}) => {
  const ptfSituationForInsurance =
    filing.tax_situation?.details?.PTF?.situation;
  const hasHealthInsurance =
    ptfSituationForInsurance?.healthInsurance?.value !== undefined;

  if (!hasHealthInsurance) return null;

  // Handle both string ("yes"/"no") and boolean (true/false) values
  const healthInsuranceValue = ptfSituationForInsurance.healthInsurance.value;
  const isYes = healthInsuranceValue === true || healthInsuranceValue === "yes";
  const isNo = healthInsuranceValue === false || healthInsuranceValue === "no";

  const hasMarketplace =
    isYes &&
    ptfSituationForInsurance.healthInsurance.fields?.marketplace?.value ===
      true;
  const hasExemption =
    isNo &&
    ptfSituationForInsurance.healthInsurance.fields?.exemptionsOrStateCoverage
      ?.value === true;

  const insuranceTaxYears = ptfSituationForInsurance?.taxYears;
  const insuranceTaxYearsArray = Array.isArray(insuranceTaxYears)
    ? insuranceTaxYears
    : insuranceTaxYears
    ? [insuranceTaxYears]
    : [];
  const insuranceTaxYearsNumeric = insuranceTaxYearsArray.map((year) =>
    Number(year),
  );

  const getDocsByType = (docType) =>
    documents.filter((doc) => {
      const type = doc.documentType?.toUpperCase();
      const docYear = doc.documentYear ? Number(doc.documentYear) : null;
      return (
        type === docType &&
        docYear !== null &&
        insuranceTaxYearsNumeric.includes(docYear)
      );
    });

  const marketplaceDocs = getDocsByType("FORM_1095_A");
  const exemptionDocs = getDocsByType("HEALTH_COVERAGE_EXEMPTION");

  const insuranceMetrics = filing.summary_metrics?.insurance_ptf || {};
  const marketplaceMetrics = insuranceMetrics.form_1095_a || {
    required: 0,
    provided: 0,
    complete: true,
  };
  const exemptionMetricsData = insuranceMetrics.health_coverage_exemption || {
    required: 0,
    provided: 0,
    complete: true,
  };

  const insuranceSectionComplete = insuranceMetrics.complete ?? false;
  const insuranceSectionNeedsAttention =
    insuranceMetrics.attention_needed_cus ?? false;
  const insuranceIsWaiting =
    isCpa && !insuranceSectionComplete && insuranceSectionNeedsAttention;

  return (
    <AccordionEditable
      key="insurance"
      title="Insurance"
      hasRequiredActions={!isCpa && insuranceSectionNeedsAttention}
      allActionsComplete={insuranceSectionComplete}
      isWaiting={insuranceIsWaiting}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <ContentCard>
          <div className="section-header-row">
            {insuranceSectionNeedsAttention && (
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
                alt="Attention needed"
                className="attention-indicator-icon"
              />
            )}
            <h4 className="section-header-title">Health Insurance</h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              fontSize: "14px",
              color: "#1A2127",
              width:"100%"
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#666" }}>
                Had Health Insurance All Year:
              </span>
              <span>{isYes ? "Yes" : "No"}</span>
            </div>

            {isYes &&
              ptfSituationForInsurance.healthInsurance.fields?.marketplace
                ?.value !== undefined && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <span style={{ fontWeight: "600", color: "#666" }}>
                    Marketplace:
                  </span>
                  <span>
                    {ptfSituationForInsurance.healthInsurance.fields.marketplace
                      .value === true
                      ? "Yes"
                      : "No"}
                  </span>
                </div>
              )}

            {isNo &&
              ptfSituationForInsurance.healthInsurance.fields
                ?.exemptionsOrStateCoverage?.value !== undefined && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <span style={{ fontWeight: "600", color: "#666" }}>
                    Exemptions or State Coverage:
                  </span>
                  <span>
                    {ptfSituationForInsurance.healthInsurance.fields
                      .exemptionsOrStateCoverage.value === true
                      ? "Yes"
                      : "No"}
                  </span>
                </div>
              )}

            {isYes && !hasMarketplace && (
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <span className="tax-situation-meta-text">
                  Taxpayer does not have Form 1095-A
                </span>
              </div>
            )}

            {hasMarketplace && (
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <div
                  className="section-meta-row"
                  style={{ marginBottom: "8px" }}
                >
                  <span className="section-meta-item">
                    <span className="section-meta-label">
                      Form 1095-A Required:
                    </span>
                    <span className="section-meta-value">
                      {marketplaceMetrics.required}
                    </span>
                  </span>
                  <span className="section-meta-item">
                    <span className="section-meta-label">Submitted:</span>
                    <span className="section-meta-value">
                      {marketplaceMetrics.provided}
                    </span>
                  </span>
                </div>
                {marketplaceDocs.length > 0 && (
                  <DocumentList
                    documents={marketplaceDocs}
                    onDocumentDeleted={handleDocumentDeleted}
                    isCpa={isCpa}
                    disableDelete={!isCpa && allEfilesComplete}
                  />
                )}
                {!isCpa && !marketplaceMetrics.complete && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "8px",
                    }}
                  >
                    <button
                      type="button"
                      className="cta-button primary"
                      style={{ width: "auto" }}
                      onClick={() =>
                        handleOpenUploadModal({
                          name: "healthInsuranceMarketplace",
                          label: "Form 1095-A (Marketplace)",
                        })
                      }
                    >
                      Upload Form 1095-A
                    </button>
                  </div>
                )}
              </div>
            )}

            {hasExemption && (
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <div
                  className="section-meta-row"
                  style={{ marginBottom: "8px" }}
                >
                  <span className="section-meta-item">
                    <span className="section-meta-label">
                      Exemption Document Required:
                    </span>
                    <span className="section-meta-value">
                      {exemptionMetricsData.required}
                    </span>
                  </span>
                  <span className="section-meta-item">
                    <span className="section-meta-label">Submitted:</span>
                    <span className="section-meta-value">
                      {exemptionMetricsData.provided}
                    </span>
                  </span>
                </div>
                {exemptionDocs.length > 0 && (
                  <DocumentList
                    documents={exemptionDocs}
                    onDocumentDeleted={handleDocumentDeleted}
                    isCpa={isCpa}
                    disableDelete={!isCpa && allEfilesComplete}
                  />
                )}
                {!isCpa && !exemptionMetricsData.complete && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "8px",
                    }}
                  >
                    <button
                      type="button"
                      className="cta-button primary"
                      style={{ width: "auto" }}
                      onClick={() =>
                        handleOpenUploadModal({
                          name: "healthInsuranceExemption",
                          label: "Health Coverage Exemption",
                        })
                      }
                    >
                      Upload Document
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </ContentCard>
      </div>
    </AccordionEditable>
  );
};

export default InsuranceSection;
